import {supabase} from '../supabase/supabase';

const CACHE_VERSION='v8';
const CURATIONS_KEY=`curateed_curations_${CACHE_VERSION}`;
const PAGE_PREFIX=`curateed_page_${CACHE_VERSION}_`;
const CACHE_TTL=15 * 60 * 1000;
const PAGE_COLUMNS=[
  'id',
  'slug',
  'h1_title',
  'page_topic',
  'intro_text',
  'category',
  'pinterest_image_url',
  'pinterest_image_alt',
  'meta_description',
  'courses',
  'is_published',
  'created_at'
].join(',');

let curationsRequest=null;
const pageRequests=new Map();

function readCache(key) {
  if (typeof window==='undefined') return null;

  try {
    const cached=localStorage.getItem(key);
    if (!cached) return null;

    const parsed=JSON.parse(cached);
    if (!parsed || (!Array.isArray(parsed.value) && !parsed.value)) return null;

    return {
      value: parsed.value,
      fresh: Date.now() - parsed.savedAt < CACHE_TTL
    };
  } catch {
    return null;
  }
}

function writeCache(key,value) {
  if (typeof window==='undefined') return;

  try {
    localStorage.setItem(key,JSON.stringify({
      value,
      savedAt: Date.now()
    }));
  } catch {
    // Storage can be unavailable in private browsing.
  }
}

function getPrerenderedPage(slug) {
  if (
    typeof globalThis!=='undefined' &&
    globalThis.__CURATEED_PRERENDER_PAGE__?.slug===slug
  ) {
    return globalThis.__CURATEED_PRERENDER_PAGE__;
  }

  if (
    typeof window!=='undefined' &&
    window.__CURATEED_PAGE__?.slug===slug
  ) {
    return window.__CURATEED_PAGE__;
  }

  return null;
}

function normalizePage(page) {
  if (!page) return null;

  return {
    ...page,
    slug: page.slug || page.page_slug,
    h1_title: page.h1_title || page.page_h1_title || '',
    page_topic: page.page_topic?.trim() || '',
    intro_text: page.intro_text || page.page_intro_text || '',
    meta_description:
      page.meta_description ||
      page.page_meta_description ||
      page.intro_text ||
      page.page_intro_text ||
      '',
    pinterest_image_url:
      page.pinterest_image_url ||
      page.page_pinterest_image_url ||
      '',
    pinterest_image_alt:
      page.pinterest_image_alt ||
      page.page_pinterest_image_alt ||
      '',
    courses: Array.isArray(page.courses) ? page.courses : []
  };
}

function mapCuration(item) {
  const page=normalizePage(item);

  return {
    id: page.id,
    slug: page.slug,
    title: page.h1_title,
    description: page.intro_text,
    tag: page.category,
    image: page.pinterest_image_url,
    imageAlt: page.pinterest_image_alt
  };
}

export function getCachedCurations() {
  return readCache(CURATIONS_KEY)?.value || [];
}

export function getCachedPage(slug) {
  const prerendered=getPrerenderedPage(slug);
  if (prerendered) return normalizePage(prerendered);

  const cached=readCache(`${PAGE_PREFIX}${slug}`)?.value;
  return cached ? normalizePage(cached) : null;
}

export async function refreshCurations({force=false}={}) {
  const cached=readCache(CURATIONS_KEY);

  if (!force && cached?.fresh && cached.value.length > 0) {
    return cached.value;
  }

  if (curationsRequest) return curationsRequest;

  curationsRequest=(async ()=> {
    const {data,error}=await supabase
      .from('categories_pages')
      .select(PAGE_COLUMNS)
      .eq('is_published',true)
      .order('created_at',{ascending: false});

    if (error) {
      console.error('Failed to load published collections:',error);
      return cached?.value || [];
    }

    const pages=(data || []).map(normalizePage);
    const curations=pages.map(mapCuration);

    writeCache(CURATIONS_KEY,curations);

    pages.forEach((page)=> {
      writeCache(`${PAGE_PREFIX}${page.slug}`,page);
    });

    return curations;
  })().finally(()=> {
    curationsRequest=null;
  });

  return curationsRequest;
}

export async function fetchPage(slug,{force=false}={}) {
  const prerendered=getPrerenderedPage(slug);

  if (!force && prerendered) {
    return normalizePage(prerendered);
  }

  const cacheKey=`${PAGE_PREFIX}${slug}`;
  const cached=readCache(cacheKey);

  if (!force && cached?.fresh) {
    return normalizePage(cached.value);
  }

  if (pageRequests.has(slug)) {
    return pageRequests.get(slug);
  }

  const request=(async ()=> {
    const {data,error}=await supabase
      .from('categories_pages')
      .select(PAGE_COLUMNS)
      .eq('slug',slug)
      .eq('is_published',true)
      .single();

    if (error || !data) {
      if (error && error.code!=='PGRST116') {
        console.error(`Failed to load collection "${slug}":`,error);
      }

      return cached?.value ? normalizePage(cached.value) : null;
    }

    const page=normalizePage(data);
    writeCache(cacheKey,page);

    return page;
  })().finally(()=> {
    pageRequests.delete(slug);
  });

  pageRequests.set(slug,request);
  return request;
}

export function prefetchPage(slug) {
  if (!slug || getPrerenderedPage(slug)) return;
  if (readCache(`${PAGE_PREFIX}${slug}`)?.fresh) return;

  fetchPage(slug).catch(()=> {});
}